import classes from "./KioskPages/SlideShowKiosk.module.css";
import axios from "axios";
import { path, buildMediaUrlForStoredPath } from "../../../routes";
import {useEffect, useMemo, useRef, useState} from "react";

/** Jen skutečné LG webOS / NetCast (ne obecné „SmartTV“ – to omylem přepínalo URL i mimo LG). */
const isWebOsLikeClient = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return /Web0S|webOS|WebOS\.TV|NetCast|LG Browser|LG SimpleSmart|LGE;/i.test(ua);
};

/** Z DB cesty typu …_webos.mp4 odvodit cestu k originálnímu .mp4 (fallback při chybě přehrávače). */
const stripWebOsSuffixFromStoredPath = (p) => {
    const s = String(p || '').trim();
    if (!/_webos\.mp4$/i.test(s)) return s;
    return s.replace(/_webos\.mp4$/i, '.mp4');
};

const SlideShowKiosk_redesign2 = (props) => {
    /** Násobitel měřítka web/PDF iframe (pod 1 = oddálit kalendář, nad 1 = přiblížit). Rozsah 0.5–3. */
    const webEmbedZoom =
        typeof props.webEmbedZoom === 'number' && Number.isFinite(props.webEmbedZoom) && props.webEmbedZoom > 0
            ? Math.min(3, Math.max(0.5, props.webEmbedZoom))
            : 1;
    /** Posun iframe oproti středu (px); kladné = níž na displeji */
    const webEmbedOffsetY =
        typeof props.webEmbedOffsetY === 'number' && Number.isFinite(props.webEmbedOffsetY)
            ? props.webEmbedOffsetY
            : 56;
    const parseGroup = (v) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
    };
    const group = parseGroup(props.groupID);
    const videoGroupId = parseGroup(props.videoGroupId);
    const slideshowGroupId = parseGroup(props.slideshowGroupId);
    const slideshowGroupIds = Array.isArray(props.slideshowGroupIds)
        ? props.slideshowGroupIds
            .map((x) => parseGroup(x))
            .filter((x) => x != null)
        : [];
    const [mediaItems, setMediaItems] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const mediaItemsRef = useRef([]);
    const currentIndexRef = useRef(0);
    const slideIntervalMs = Math.max(1000, Number(props.intervalMs || 15000));
    const fit = (props.objectFit === 'cover' || props.objectFit === 'contain') ? props.objectFit : 'contain';
    const bg = String(props.backgroundColor || '#ffffff');
    const desktopViewport = useMemo(() => ({ w: 1920, h: 1080 }), []);
    const webWrapRef = useRef(null);
    const [webScale, setWebScale] = useState(1);
    const [webReloadToken, setWebReloadToken] = useState(0);
    /** Klíč = DB cesta k souboru; hodnota = Set kroků ('webos' | 'orig') — zabrání ping-pongu mezi originálem a _webos. */
    const videoFallbackStepsRef = useRef(Object.create(null));
    const kioskVideoRef = useRef(null);
    const WEB_RELOAD_MS = 5 * 60 * 1000;
    const VIDEO_EXT_RE = /\.(mp4|webm|mov|m4v|avi|mkv)(\?.*)?$/i;
    const PDF_EXT_RE = /\.pdf(\?.*)?$/i;
    const isVideoPath = (p) => VIDEO_EXT_RE.test(String(p || '').trim());
    const isPdfPath = (p) => PDF_EXT_RE.test(String(p || '').trim());
    const isHttpUrl = (p) => /^https?:\/\//i.test(String(p || '').trim());
    const withCurrentWeekDateParam = (urlStr) => {
        try {
            const s = String(urlStr || '').trim();
            if (!isHttpUrl(s)) return s;
            const u = new URL(s);
            // Only rewrite known weekly calendar param `d=dd.mm.yyyy` (e.g. rozpis zimni stadion)
            const d = u.searchParams.get('d');
            if (!d || !/^\d{2}\.\d{2}\.\d{4}$/.test(d)) return s;
            const now = new Date();
            const day = now.getDay(); // 0=Sun..6=Sat
            const diffToMon = (day + 6) % 7; // 0 if Monday
            const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diffToMon);
            const dd = String(mon.getDate()).padStart(2, '0');
            const mm = String(mon.getMonth() + 1).padStart(2, '0');
            const yyyy = String(mon.getFullYear());
            u.searchParams.set('d', `${dd}.${mm}.${yyyy}`);
            return u.toString();
        } catch (_) {
            return String(urlStr || '').trim();
        }
    };
    const nextIndex = (idx, len) => (len > 0 ? (idx + 1) % len : 0);

    const toWebOsVariantUrl = (rawUrl) => {
        const s = String(rawUrl || '').trim();
        if (!s) return s;
        const qIdx = s.indexOf('?');
        const base = qIdx >= 0 ? s.slice(0, qIdx) : s;
        const query = qIdx >= 0 ? s.slice(qIdx) : '';
        if (!/\.(mp4|webm|mov|m4v|avi|mkv)$/i.test(base)) return s;
        if (/_webos\.mp4$/i.test(base)) return s;
        return base.replace(/\.(mp4|webm|mov|m4v|avi|mkv)$/i, '_webos.mp4') + query;
    };

    const videoSurfaceStyle = useMemo(() => {
        const base = {
            objectFit: fit,
            width: '100%',
            height: '100%',
            minHeight: 1,
            minWidth: 1,
            backgroundColor: '#000000'
        };
        if (!isWebOsLikeClient()) return base;
        return {
            ...base,
            transform: 'translateZ(0)',
            WebkitTransform: 'translateZ(0)',
            willChange: 'transform'
        };
    }, [fit]);
    const mediaSignature = (arr) =>
        (Array.isArray(arr) ? arr : [])
            .map((x) => `${x?.mediaType || ''}|${x?.Body?.file || ''}`)
            .join('||');

    const mapRowsToMedia = (rows, mode = 'all') => {
        if (!Array.isArray(rows)) return [];
        const version = Date.now();
        const videos = [];
        const images = [];
        for (let i = 0; i < rows.length; i++) {
            const relPath = rows[i].path;
            if (!relPath) continue;
            try {
                const directUrl = String(relPath || '').trim();
                const baseUrl = isHttpUrl(directUrl)
                    ? withCurrentWeekDateParam(directUrl)
                    : buildMediaUrlForStoredPath(directUrl);
                const mediaType = isVideoPath(relPath)
                    ? 'video'
                    : isPdfPath(relPath)
                        ? 'pdf'
                        : (isHttpUrl(relPath) ? 'web' : 'image');
                if (mode === 'video' && mediaType !== 'video') continue;
                if (mode === 'image' && mediaType !== 'image') continue;
                let playbackBase = baseUrl;
                if (
                    mediaType === 'video' &&
                    !isHttpUrl(directUrl) &&
                    !/_webos\.mp4/i.test(String(relPath).trim()) &&
                    isWebOsLikeClient()
                ) {
                    playbackBase = toWebOsVariantUrl(baseUrl);
                }
                const mediaURL =
                    playbackBase + (playbackBase.includes('?') ? '&' : '?') + 'v=' + version;
                const temp = {
                    id: rows[i].id,
                    Header: rows[i].description,
                    CreatedBy: rows[i].username,
                    mediaType,
                    Body: {
                        file: relPath,
                        mediaURL
                    }
                };
                if (temp.mediaType === 'video') videos.push(temp);
                else images.push(temp);
            } catch (error) {
                console.error('Fetching elevators Error:', error.message);
            }
        }
        // Required playback order: video block -> slideshow block -> repeat
        return [...videos, ...images];
    };

    /** Poslední úspěšná odpověď getCardsV3 pro skupinu – při výpadku API / offline se znovu použije. */
    const cacheKeyForGroup = (orgId, gr) =>
        `kiosk_cards_v1_${String(orgId || 'noorg').replace(/[^a-z0-9_-]/gi, '_')}_${Number(gr)}`;

    /** null = žádný snapshot; [] = v DB opravdu nic (uloženo při posledním úspěchu) */
    const loadCachedGroupRows = (orgId, gr) => {
        try {
            if (typeof localStorage === 'undefined') return null;
            const raw = localStorage.getItem(cacheKeyForGroup(orgId, gr));
            if (raw == null) return null;
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed?.rows) ? parsed.rows : [];
        } catch (_) {
            return null;
        }
    };

    const saveCachedGroupRows = (orgId, gr, rows) => {
        try {
            if (typeof localStorage === 'undefined') return;
            localStorage.setItem(
                cacheKeyForGroup(orgId, gr),
                JSON.stringify({ rows: Array.isArray(rows) ? rows : [], ts: Date.now() })
            );
        } catch (_) {}
    };

    /** Po chybě videa smazat snapshot karet – často obsahuje starou path před překódem / po změně souboru na disku. */
    const wipeKioskCardCache = () => {
        try {
            if (typeof localStorage === 'undefined') return;
            const orgId = props.org;
            const gids = new Set(
                [group, videoGroupId, slideshowGroupId, ...slideshowGroupIds]
                    .filter((x) => x != null && Number(x) > 0)
                    .map((x) => Number(x))
            );
            gids.forEach((gr) => localStorage.removeItem(cacheKeyForGroup(orgId, gr)));
        } catch (_) {}
    };

    const fetchGroupCards = async (gr) => {
        const orgId = props.org;
        if (gr == null || gr === undefined || Number(gr) <= 0) {
            return { rows: [], networkError: false };
        }
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            const cached = loadCachedGroupRows(orgId, gr);
            if (cached === null) {
                return { rows: [], networkError: true };
            }
            return { rows: cached, networkError: false };
        }
        try {
            const response = await axios.get(
                `${path}/getCardsV3?orgID=${props.org}&group=${gr}&onlyVisible=1&device_group=2`
            );
            const rows = Array.isArray(response.data) ? response.data : [];
            saveCachedGroupRows(orgId, gr, rows);
            return { rows, networkError: false };
        } catch (error) {
            console.error('[SlideShowKiosk] Error loading cards:', error?.message || error);
            const cached = loadCachedGroupRows(orgId, gr);
            if (cached === null) {
                return { rows: [], networkError: true };
            }
            return { rows: cached, networkError: true };
        }
    };

    const applyMergedItems = (merged, prevItems) => {
        const prevIdx = currentIndexRef.current || 0;
        const prevItem = prevItems.length ? prevItems[prevIdx % prevItems.length] : null;
        const prevSig = mediaSignature(prevItems);
        const nextSig = mediaSignature(merged);
        if (prevSig === nextSig) return;
        setMediaItems(merged);
        if (prevItem?.Body?.file) {
            const sameIdx = merged.findIndex(
                (it) =>
                    it?.mediaType === prevItem.mediaType && it?.Body?.file === prevItem.Body.file
            );
            setCurrentIndex(sameIdx >= 0 ? sameIdx : 0);
        } else {
            setCurrentIndex(0);
        }
    };

    const refreshMedia = async () => {
        let anyNetworkError = false;
        const unwrap = (r) => {
            if (r && r.networkError) anyNetworkError = true;
            return r && Array.isArray(r.rows) ? r.rows : [];
        };

        if (videoGroupId || slideshowGroupId || slideshowGroupIds.length > 0) {
            const uniqSlideGroups = Array.from(
                new Set([...(slideshowGroupId ? [slideshowGroupId] : []), ...slideshowGroupIds])
            );
            let merged = [];
            if (videoGroupId) {
                const [videoRowsResult, ...slideRowsByGroup] = await Promise.all([
                    fetchGroupCards(videoGroupId),
                    ...uniqSlideGroups.map((gid) => fetchGroupCards(gid))
                ]);
                merged = [
                    ...mapRowsToMedia(unwrap(videoRowsResult), 'video'),
                    ...mapRowsToMedia(slideRowsByGroup.flatMap((r) => unwrap(r)), 'image')
                ];
            } else {
                const slideRowsByGroup = await Promise.all(uniqSlideGroups.map((gid) => fetchGroupCards(gid)));
                merged = slideRowsByGroup.flatMap((r) => mapRowsToMedia(unwrap(r), 'all'));
            }
            const prevItems = mediaItemsRef.current || [];
            if (merged.length === 0 && prevItems.length > 0 && anyNetworkError) {
                console.warn('[SlideShowKiosk] Nedostupný server / síť; ponechávám poslední slideshow.');
                return;
            }
            applyMergedItems(merged, prevItems);
            return;
        }
        if (!group) {
            setMediaItems([]);
            setCurrentIndex(0);
            return;
        }
        const rowsResult = await fetchGroupCards(group);
        anyNetworkError = Boolean(rowsResult.networkError);
        const merged = mapRowsToMedia(rowsResult.rows, 'all');
        const prevItems = mediaItemsRef.current || [];
        if (merged.length === 0 && prevItems.length > 0 && anyNetworkError) {
            console.warn('[SlideShowKiosk] Nedostupný server / síť; ponechávám poslední slideshow.');
            return;
        }
        applyMergedItems(merged, prevItems);
    };

    useEffect(() => {
        refreshMedia();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [group, videoGroupId, slideshowGroupId, slideshowGroupIds.join(','), props.org]);

    useEffect(() => {
        if (props.refreshTrigger != null && props.refreshTrigger > 0) {
            refreshMedia();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.refreshTrigger]);

    useEffect(() => {
        if (mediaItems.length === 0) return;
        setCurrentIndex((prev) => (prev < mediaItems.length ? prev : 0));
    }, [mediaItems.length]);

    useEffect(() => {
        mediaItemsRef.current = mediaItems;
    }, [mediaItems]);

    useEffect(() => {
        currentIndexRef.current = currentIndex;
    }, [currentIndex]);

    const len = mediaItems.length;
    const safeCurrent = len ? currentIndex % len : 0;
    const currentItem = len ? mediaItems[safeCurrent] : null;

    useEffect(() => {
        // Reload web/PDF content on a fixed cadence (5 min), not every slide switch.
        if (!currentItem || (currentItem.mediaType !== 'web' && currentItem.mediaType !== 'pdf')) return;
        setWebReloadToken(Date.now());
        const t = setInterval(() => {
            setWebReloadToken(Date.now());
        }, WEB_RELOAD_MS);
        return () => clearInterval(t);
    }, [currentItem?.mediaType, currentItem?.Body?.file]);

    useEffect(() => {
        // Keep embedded web/PDF slides in "desktop mode" by giving them a large virtual viewport
        // and scaling them down to fit the kiosk container.
        if (!currentItem || (currentItem.mediaType !== 'web' && currentItem.mediaType !== 'pdf')) return;
        const el = webWrapRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => {
            const rect = el.getBoundingClientRect();
            const base = Math.min(rect.width / desktopViewport.w, rect.height / desktopViewport.h);
            const scale = base * webEmbedZoom;
            setWebScale(Number.isFinite(scale) && scale > 0 ? scale : 1);
        });
        ro.observe(el);
        // initial
        const rect = el.getBoundingClientRect();
        const base = Math.min(rect.width / desktopViewport.w, rect.height / desktopViewport.h);
        const scale = base * webEmbedZoom;
        setWebScale(Number.isFinite(scale) && scale > 0 ? scale : 1);
        return () => ro.disconnect();
    }, [currentItem?.mediaType, desktopViewport.w, desktopViewport.h, webEmbedZoom]);

    useEffect(() => {
        // Keep syncing DB updates even during video playback (async transcode may switch origin -> *_webos.mp4).
        const refreshMs = isWebOsLikeClient() ? 6000 : 12000;
        const refreshInterval = setInterval(() => {
            if (typeof navigator !== 'undefined' && navigator.onLine) {
                refreshMedia();
            }
        }, refreshMs);
        return () => clearInterval(refreshInterval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [group, videoGroupId, slideshowGroupId, slideshowGroupIds.join(','), props.org]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onOnline = () => {
            refreshMedia();
        };
        window.addEventListener('online', onOnline);
        return () => window.removeEventListener('online', onOnline);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [group, videoGroupId, slideshowGroupId, slideshowGroupIds.join(','), props.org]);

    useEffect(() => {
        if (!currentItem || currentItem.mediaType === 'video') return;
        const t = setTimeout(() => {
            setCurrentIndex((prev) => nextIndex(prev, Math.max(mediaItems.length, 1)));
        }, slideIntervalMs);
        return () => clearTimeout(t);
    }, [currentItem, slideIntervalMs, mediaItems.length]);

    // webOS / vestavěné Chromium často ignorují jen autoPlay — vynutit load() + play() po změně zdroje.
    useEffect(() => {
        const v = kioskVideoRef.current;
        if (!v || currentItem?.mediaType !== 'video' || !currentItem?.Body?.mediaURL) return undefined;

        let cancelled = false;
        const tryPlay = () => {
            if (cancelled || !v) return;
            try {
                v.muted = true;
                const p = v.play();
                if (p && typeof p.catch === 'function') {
                    p.catch(() => {
                        if (cancelled) return;
                        window.setTimeout(() => {
                            if (cancelled || !v) return;
                            v.play().catch(() => {});
                        }, 300);
                    });
                }
            } catch (_) {}
        };

        try {
            v.setAttribute('playsinline', '');
            v.setAttribute('webkit-playsinline', '');
        } catch (_) {}

        const t = window.setTimeout(() => {
            if (cancelled || !v) return;
            try {
                v.load();
            } catch (_) {}
            tryPlay();
        }, 0);

        const onCanPlay = () => tryPlay();
        const onLoadedData = () => tryPlay();
        const onLoadedMetadata = () => tryPlay();
        const onStalled = () => tryPlay();
        v.addEventListener('canplay', onCanPlay);
        v.addEventListener('loadeddata', onLoadedData);
        v.addEventListener('loadedmetadata', onLoadedMetadata);
        v.addEventListener('stalled', onStalled);

        return () => {
            cancelled = true;
            window.clearTimeout(t);
            v.removeEventListener('canplay', onCanPlay);
            v.removeEventListener('loadeddata', onLoadedData);
            v.removeEventListener('loadedmetadata', onLoadedMetadata);
            v.removeEventListener('stalled', onStalled);
            try {
                v.pause();
            } catch (_) {}
        };
    }, [currentItem?.mediaType, currentItem?.Body?.mediaURL]);

    return <div className={classes.mainRed} role="img" aria-label="Kiosk slideshow" style={{ backgroundColor: bg }}>
        {currentItem?.mediaType === 'image' && currentItem?.Body?.mediaURL && (
            <img
                className={classes.imageRed}
                src={currentItem.Body.mediaURL}
                alt=""
                style={{ objectFit: fit, width: '100%', height: '100%', backgroundColor: bg }}
            />
        )}
        {currentItem?.mediaType === 'video' && currentItem?.Body?.mediaURL && (
            <video
                ref={kioskVideoRef}
                key={currentItem.Body.mediaURL}
                className={classes.imageRed}
                src={currentItem.Body.mediaURL}
                autoPlay
                muted
                playsInline
                preload="auto"
                controls={false}
                onEnded={(e) => {
                    // Single-video group: replay the same video endlessly.
                    if (mediaItems.length <= 1) {
                        try {
                            e.currentTarget.currentTime = 0;
                            const p = e.currentTarget.play();
                            if (p && typeof p.catch === 'function') p.catch(() => {});
                        } catch (_) {}
                        return;
                    }
                    setCurrentIndex((prev) => nextIndex(prev, Math.max(mediaItems.length, 1)));
                }}
                onError={() => {
                    const failedUrl = String(currentItem?.Body?.mediaURL || '').trim();
                    const dbPath = String(currentItem?.Body?.file || '').trim();
                    const stepsMap = videoFallbackStepsRef.current;
                    if (dbPath && isVideoPath(dbPath) && !isHttpUrl(dbPath)) {
                        if (!stepsMap[dbPath]) stepsMap[dbPath] = new Set();
                        const steps = stepsMap[dbPath];
                        const isWebosUrl = /_webos\.mp4/i.test(failedUrl);
                        if (!isWebosUrl && !steps.has('webos')) {
                            const webosUrl = toWebOsVariantUrl(failedUrl);
                            if (webosUrl && webosUrl !== failedUrl) {
                                steps.add('webos');
                                setMediaItems((prev) => prev.map((it) => {
                                    if (it?.Body?.mediaURL !== failedUrl) return it;
                                    return {
                                        ...it,
                                        Body: {
                                            ...it.Body,
                                            mediaURL: webosUrl
                                        }
                                    };
                                }));
                                return;
                            }
                        }
                        if (isWebosUrl && !steps.has('orig')) {
                            const pathForOrig = stripWebOsSuffixFromStoredPath(dbPath);
                            const origBase = buildMediaUrlForStoredPath(pathForOrig);
                            const origUrl =
                                origBase + (origBase.includes('?') ? '&' : '?') + 'v=' + Date.now();
                            if (origUrl !== failedUrl) {
                                steps.add('orig');
                                setMediaItems((prev) => prev.map((it) => {
                                    if (it?.Body?.mediaURL !== failedUrl) return it;
                                    return {
                                        ...it,
                                        Body: {
                                            ...it.Body,
                                            mediaURL: origUrl
                                        }
                                    };
                                }));
                                return;
                            }
                        }
                    }
                    wipeKioskCardCache();
                    void refreshMedia();
                    if (mediaItems.length > 1) {
                        setCurrentIndex((prev) => nextIndex(prev, Math.max(mediaItems.length, 1)));
                    }
                }}
                style={videoSurfaceStyle}
            />
        )}
        {currentItem?.mediaType === 'pdf' && currentItem?.Body?.mediaURL && (
            <div
                ref={webWrapRef}
                className={classes.imageRed}
                style={{ backgroundColor: bg, overflow: 'hidden' }}
            >
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        width: desktopViewport.w,
                        height: desktopViewport.h,
                        transform: `translate(-50%, calc(-50% + ${webEmbedOffsetY}px)) scale(${webScale})`,
                        transformOrigin: 'center center'
                    }}
                >
                    <iframe
                        src={currentItem.Body.mediaURL + (String(currentItem.Body.mediaURL).includes('?') ? '&' : '?') + '_r=' + webReloadToken}
                        title="PDF slide"
                        style={{ width: desktopViewport.w, height: desktopViewport.h, border: 0, backgroundColor: bg }}
                    />
                </div>
            </div>
        )}
        {currentItem?.mediaType === 'web' && currentItem?.Body?.mediaURL && (
            <div
                ref={webWrapRef}
                className={classes.imageRed}
                style={{ backgroundColor: bg, overflow: 'hidden' }}
            >
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        width: desktopViewport.w,
                        height: desktopViewport.h,
                        transform: `translate(-50%, calc(-50% + ${webEmbedOffsetY}px)) scale(${webScale})`,
                        transformOrigin: 'center center'
                    }}
                >
                    <iframe
                        src={currentItem.Body.mediaURL + (String(currentItem.Body.mediaURL).includes('?') ? '&' : '?') + '_r=' + webReloadToken}
                        title="Web slide"
                        style={{ width: desktopViewport.w, height: desktopViewport.h, border: 0, backgroundColor: bg }}
                    />
                </div>
            </div>
        )}
    </div>
}
export default SlideShowKiosk_redesign2